import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  CustomerComplianceDocument,
  customersApi,
} from '@texas/api/endpoints/customersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { customerComplianceDocumentEvents } from './events';

export function useCustomerComplianceDocumentBranches(
  customerId: number,
  document: CustomerComplianceDocument,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const disclosure = useValueDisclosure<number[]>();
  const selectedBranchIds = disclosure.value ?? [];
  const {
    request: updateBranches,
    loading,
    error,
  } = useApiRequest(customersApi.updateComplianceDocumentBranches);
  const currentBranchIds = document.branches.map((branch) => branch.id);
  const hasChanges =
    selectedBranchIds.length !== currentBranchIds.length ||
    selectedBranchIds.some((branchId) => !currentBranchIds.includes(branchId));

  const open = () => {
    disclosure.onOpen(currentBranchIds);
  };

  const selectBranches = (values: string | string[]) => {
    const branchIds = Array.isArray(values) ? values : [values];
    disclosure.onOpen(branchIds.map(Number));
  };

  const apply = async () => {
    const response = await updateBranches(
      customerId,
      document.complianceDocumentDefinitionId,
      { branchIds: selectedBranchIds },
    );
    if (response.hasError) return;

    toast({
      title: t('general.updated'),
      status: 'success',
      isClosable: true,
    });
    customerComplianceDocumentEvents.changed.dispatch(customerId);
    disclosure.onClose();
  };

  return {
    apply,
    close: disclosure.onClose,
    error,
    hasChanges,
    isOpen: disclosure.isOpen,
    loading,
    open,
    selectedBranchIds,
    selectBranches,
  };
}
